import { createContext, useContext } from 'react';

import { ApolloClient } from '@apollo/client';
import { createStore, StoreApi, useStore } from 'zustand';

import {
  GetProductsByIdBatchedQuery,
  WishlistForCurrentUserQuery,
  WishlistForCurrentUserQueryVariables,
  WishlistSortBy,
  WishlistSortByOption,
} from '@notino/shared/definitions/types';

import { FILTERS_MIN_PRODUCTS_COUNT, WISHLIST_PAGE_SIZE } from './constants';
import wishlistForCurrentUser from './queries/wishlistForCurrentUser.graphql';

export type WishlistStoreProduct =
  GetProductsByIdBatchedQuery['vpProductByIds'][number];

type WishlistState = {
  productIds: number[]; // navigation api might return null products, so we have this productIds to do pagination properly
  products: WishlistStoreProduct[];
  hasMore: boolean;
  isLoading: boolean;
  totalProductCount: number;
  totalFilteredProductCount: number;
  isSharedWishlist: boolean;

  sortByOptions: WishlistSortByOption[];
  selectedSortBy: WishlistSortBy;

  filtration: NonNullable<
    WishlistForCurrentUserQuery['WishlistForCurrentUser']
  >['filtration'];
  selectedNavigationValues: number[];

  selectionMode: boolean;
  selectedIds: string[];

  actions: {
    setProductWishlisted: (productId: string, wishlisted: boolean) => void;
    setSelectedSortBy: (sortBy: WishlistSortBy) => void;
    setSelectedNavigationValues: (values: number[]) => void;
    toggleSelectionMode: () => void;
    setProductSelected: (productId: string, selected: boolean) => void;
    bulkRemoveProducts: (productIds: string[]) => void;
    fetchProducts: (
      variables: Omit<WishlistForCurrentUserQueryVariables, 'count'>
    ) => Promise<void>;
    insertFirstProduct: (product: WishlistStoreProduct) => void;
  };
};

type WishlistStore = StoreApi<WishlistState>;

const shouldResetFilteringAfterRemoval = ({
  totalFilteredProductCount,
  totalProductCount,
  selectedNavigationValues,
}: Pick<
  WishlistState,
  'totalFilteredProductCount' | 'totalProductCount' | 'selectedNavigationValues'
>) => {
  if (selectedNavigationValues.length === 0) {
    return false;
  }
  if (totalFilteredProductCount === 0) {
    return true;
  }
  return totalProductCount < FILTERS_MIN_PRODUCTS_COUNT;
};

/**
 * Evicts all cached WishlistForCurrentUser queries so that
 * navigating back to a wishlist page triggers a fresh network request
 * instead of rendering stale data from Apollo cache.
 */
const evictWishlistCache = (client: ApolloClient<object>) => {
  client.cache.evict({
    id: 'ROOT_QUERY',
    fieldName: 'WishlistForCurrentUser',
  });
};

export const createWishlistStore = (
  initParams: Omit<
    WishlistState,
    | 'actions'
    | 'selectedSortBy'
    | 'selectedNavigationValues'
    | 'isLoading'
    | 'selectedIds'
  >,
  client: ApolloClient<object>,
  listIds: { listId?: string; sharedListId: string | null }
) =>
  createStore<WishlistState>((set, get) => ({
    ...initParams,
    selectedSortBy: WishlistSortBy.Default,
    selectedNavigationValues: [],
    isLoading: false,
    selectedIds: [],
    actions: {
      setSelectedSortBy: (sortBy) => {
        void get().actions.fetchProducts({
          sortBy,
          offset: 0,
        });
        set({ selectedSortBy: sortBy, productIds: [], products: [] });
      },

      setSelectedNavigationValues: (values) => {
        void get().actions.fetchProducts({
          navigationValues: values,
          offset: 0,
        });
        set({ selectedNavigationValues: values, productIds: [], products: [] });
      },

      setProductWishlisted: (productId, wishlisted) => {
        if (get().isSharedWishlist) {
          set({
            products: get().products.map((p) =>
              p?.id === String(productId) ? { ...p, wishlisted } : p
            ),
          });
          return;
        }

        // Down from here: my wishlist - can only remove products, never add

        const totalFilteredProductCount = get().totalFilteredProductCount - 1;
        const totalProductCount = get().totalProductCount - 1;

        const shouldResetFiltering = shouldResetFilteringAfterRemoval({
          totalFilteredProductCount,
          totalProductCount,
          selectedNavigationValues: get().selectedNavigationValues,
        });

        set({
          products: get().products.filter((p) => p?.id !== productId),
          productIds: get().productIds.filter((p) => p !== Number(productId)),
          totalFilteredProductCount,
          totalProductCount,
        });

        evictWishlistCache(client);

        if (shouldResetFiltering) {
          // reset filters
          get().actions.setSelectedNavigationValues([]);
        }
      },

      toggleSelectionMode: () => {
        const newSelectionMode = !get().selectionMode;
        if (newSelectionMode === false) {
          set({ selectedIds: [] });
        }
        set({ selectionMode: newSelectionMode });
      },

      setProductSelected: (productId, isSelected) => {
        if (isSelected) {
          set({ selectedIds: [...get().selectedIds, productId] });
        } else {
          set({
            selectedIds: get().selectedIds.filter((id) => id !== productId),
          });
        }
      },

      bulkRemoveProducts: (productIds) => {
        const totalFilteredProductCount =
          get().totalFilteredProductCount - productIds.length;
        const totalProductCount = get().totalProductCount - productIds.length;

        const shouldResetFiltering = shouldResetFilteringAfterRemoval({
          totalFilteredProductCount,
          totalProductCount,
          selectedNavigationValues: get().selectedNavigationValues,
        });

        set({
          productIds: get().productIds.filter(
            (id) => !productIds.includes(String(id))
          ),
          products: get().products.filter(
            (product) => !productIds.includes(String(product?.id))
          ),
          totalProductCount,
          totalFilteredProductCount,
        });

        evictWishlistCache(client);

        if (shouldResetFiltering) {
          // reset filters
          get().actions.setSelectedNavigationValues([]);
        }
      },

      insertFirstProduct: (product) => {
        set((state) => ({
          products: [product, ...state.products] as WishlistStoreProduct[],
          productIds: [Number(product?.id), ...state.productIds],
          totalProductCount: state.totalProductCount + 1,
          totalFilteredProductCount: state.totalFilteredProductCount + 1,
        }));

        evictWishlistCache(client);
      },

      fetchProducts: async ({ offset, navigationValues, sortBy }) => {
        set({ isLoading: true });
        const { data } = await client.query<
          WishlistForCurrentUserQuery,
          WishlistForCurrentUserQueryVariables
        >({
          query: wishlistForCurrentUser,
          fetchPolicy: 'no-cache',
          variables: {
            offset,
            sortBy: sortBy ?? get().selectedSortBy,
            navigationValues:
              navigationValues ?? get().selectedNavigationValues,
            count: WISHLIST_PAGE_SIZE,
            wishlist: {
              ...(listIds.listId ? { listId: listIds.listId } : {}),
              ...(listIds.sharedListId
                ? { wishlistId: listIds.sharedListId }
                : {}),
            },
          },
        });

        const { hasMore, products, sortByOptions, totalProductCount } =
          data.WishlistForCurrentUser!.listing;
        const { filtration } = data.WishlistForCurrentUser!;

        set((prev) => ({
          hasMore,
          isLoading: false,
          sortByOptions,
          totalFilteredProductCount: totalProductCount,
          filtration,
          selectedNavigationValues: get().selectedNavigationValues.filter(
            (val) =>
              filtration.some((f) =>
                f.items.some((i) => i.navigationValue === val)
              )
          ),
          products: [
            ...prev.products,
            ...(products.map((x) => x.product).filter(Boolean) as NonNullable<
              (typeof products)[number]['product']
            >[]),
          ],
          productIds: [
            ...prev.productIds,
            ...products.map((x) => x.webProductId),
          ],
        }));
      },
    },
  }));

const Context = createContext<{ store: WishlistStore }>(null!);
export const WishlistContextProvider = Context.Provider;

export const useWishlistStore = <T>(
  selector: Parameters<typeof useStore<WishlistStore, T>>[1]
) => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useWishlistStore must be used inside WishlistProvider');
  }
  return useStore(ctx?.store, selector);
};
